import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Icon, Loading, SearchInput } from '@popety-io/ui'
import { apiClient } from '@/services/apiClient'
import {
  Container,
  Header,
  BackButton,
  HeaderIcon,
  HeaderTitle,
  CloseButton,
  FormContainer,
  FormGroup,
  FormLabel,
  SearchResults,
  SearchResultItem,
  SearchResultCheckbox,
  SearchResultInfo,
  SearchResultName,
  SearchResultMeta,
  SearchResultValue,
  SelectedSummary,
  SelectedCount,
  FormFooter,
  LoadingContainer,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
} from './AttachExistingDeals.styles'
import styled from 'styled-components'

// Additional styled components for enhanced UI
const PipelineInfo = styled.div`
  font-size: 12px;
  color: #6b6b6b;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`
const PipelineBadge = styled.span`
  background: #f0f9ff;
  color: #0284c7;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
`
const StageBadge = styled.span`
  background: #f0fdf4;
  color: #16a34a;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
`
const InformationsDisplay = styled.div`
  font-size: 12px;
  color: #6b6b6b;
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`
const InfoItem = styled.span`
  background: #f5f5f5;
  padding: 2px 8px;
  border-radius: 4px;
`

interface AttachExistingDealsProps {
  landId: string
  onClose: () => void
  onSuccess: (dealIds: string[]) => void
}

interface Deal {
  id: string
  title: string
  pipeline_kanban_column_id: string
  pipeline_kanban_column_name?: string
  pipeline_kanban_name?: string
  pipeline_kanban_id?: string
  create_timestamp?: string
  status?: string
  informations?: Record<string, any>
  isArchived?: boolean
  is_archived?: boolean
  ownerUserId: string
}

export const AttachExistingDeals: React.FC<AttachExistingDealsProps> = ({
  landId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('explore')
  const [loading, setLoading] = useState(false)
  const [deals, setDeals] = useState<Deal[]>([])
  const [filteredDeals, setFilteredDeals] = useState<Deal[]>([])
  const [selectedDeals, setSelectedDeals] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [isAttaching, setIsAttaching] = useState(false)

  // Fetch user's deals on mount
  useEffect(() => {
    fetchUserDeals()
  }, [])

  // Filter deals based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredDeals(deals)
    } else {
      const query = searchQuery.toLowerCase()
      const filtered = deals.filter(
        (deal) =>
          deal.title.toLowerCase().includes(query) ||
          deal.pipeline_kanban_name?.toLowerCase().includes(query) ||
          deal.pipeline_kanban_column_name?.toLowerCase().includes(query) ||
          deal.informations?.dealType?.toLowerCase().includes(query)
      )
      setFilteredDeals(filtered)
    }
  }, [searchQuery, deals])

  const fetchUserDeals = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/pipelines/kanbandeals/my-deals')
      const userDeals = response.data || []

      // Filter to show only open (non-archived) deals
      const activeDeals = userDeals
        .filter(
          (deal: Deal) =>
            !deal.isArchived && !deal.is_archived && deal.status === 'open'
        )
        .map((deal: Deal) => ({
          ...deal,
          // Add pipeline and column names if available from the response
        }))

      setDeals(activeDeals)
      setFilteredDeals(activeDeals)
    } catch (error) {
      console.error('Error fetching user deals:', error)
      setDeals([])
      setFilteredDeals([])
    } finally {
      setLoading(false)
    }
  }

  const toggleDealSelection = (dealId: string) => {
    const newSelected = new Set(selectedDeals)
    if (newSelected.has(dealId)) {
      newSelected.delete(dealId)
    } else {
      newSelected.add(dealId)
    }
    setSelectedDeals(newSelected)
  }

  const handleAttachSelected = async () => {
    if (selectedDeals.size === 0) return

    setIsAttaching(true)
    try {
      // Attach selected deals to the land
      const dealIds = Array.from(selectedDeals)
      await Promise.all(
        dealIds.map((dealId) =>
          apiClient.post('/pipelines/pipelinekanbandealslands', {
            pipeline_kanban_deal_id: dealId,
            land_id: landId,
          })
        )
      )

      onSuccess(dealIds)
    } catch (error) {
      console.error('Error attaching deals:', error)
      alert(t('deals.attachError', 'Failed to attach deals. Please try again.'))
    } finally {
      setIsAttaching(false)
    }
  }

  const formatCurrency = (value: number | undefined) => {
    if (!value) return '-'
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Loading size="medium" />
          <div>
            {t('deals.loadingAvailableDeals', 'Loading available deals...')}
          </div>
        </LoadingContainer>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={onClose}>
          <Icon name="arrow-left" size="medium" />
        </BackButton>
        <HeaderIcon>
          <Icon name="real-estate-listing" size="medium" />
        </HeaderIcon>
        <HeaderTitle>
          {t('deals.attachExisting', 'Attach Existing Deal')}
        </HeaderTitle>
        {onClose && (
          <CloseButton onClick={onClose}>
            <Icon name="x" size="medium" />
          </CloseButton>
        )}
      </Header>

      <FormContainer>
        <FormGroup>
          <FormLabel>{t('deals.searchForDeals', 'Search for deals')}</FormLabel>
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t(
              'deals.searchPlaceholder',
              'Search by name, pipeline, or type...'
            )}
            clearable
          />
        </FormGroup>

        <FormGroup>
          <FormLabel>{t('deals.availableDeals', 'Available Deals')}</FormLabel>

          {filteredDeals.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <Icon name="search" size="large" />
              </EmptyIcon>
              <EmptyTitle>
                {searchQuery
                  ? t('deals.noMatchingDeals', 'No matching deals found')
                  : t('deals.noAvailableDeals', 'No available deals')}
              </EmptyTitle>
              <EmptyDescription>
                {searchQuery
                  ? t(
                      'deals.tryDifferentSearch',
                      'Try adjusting your search criteria'
                    )
                  : t(
                      'deals.createNewDeal',
                      'Create a new deal to get started'
                    )}
              </EmptyDescription>
            </EmptyState>
          ) : (
            <>
              <SearchResults>
                {filteredDeals.map((deal) => (
                  <SearchResultItem
                    key={deal.id}
                    $selected={selectedDeals.has(deal.id)}
                    onClick={() => toggleDealSelection(deal.id)}
                  >
                    <SearchResultCheckbox
                      $selected={selectedDeals.has(deal.id)}
                    />
                    <SearchResultInfo>
                      <SearchResultName>{deal.title}</SearchResultName>

                      <PipelineInfo>
                        {deal.pipeline_kanban_name && (
                          <PipelineBadge>
                            {deal.pipeline_kanban_name}
                          </PipelineBadge>
                        )}
                        {deal.pipeline_kanban_column_name && (
                          <>
                            <span>→</span>
                            <StageBadge>
                              {deal.pipeline_kanban_column_name}
                            </StageBadge>
                          </>
                        )}
                      </PipelineInfo>

                      {deal.informations &&
                        Object.keys(deal.informations).length > 0 && (
                          <InformationsDisplay>
                            {Object.entries(deal.informations)
                              .filter(
                                ([key]) =>
                                  ![
                                    'dealValue',
                                    'amount',
                                    'currency',
                                    'description',
                                    'summary',
                                  ].includes(key)
                              )
                              .slice(0, 3)
                              .map(([key, value]) => (
                                <InfoItem key={key}>
                                  {key.replace(/_/g, ' ')}: {String(value)}
                                </InfoItem>
                              ))}
                          </InformationsDisplay>
                        )}
                    </SearchResultInfo>
                    {(deal.informations?.dealValue ||
                      deal.informations?.amount) && (
                      <SearchResultValue>
                        {formatCurrency(
                          deal.informations.dealValue ||
                            deal.informations.amount
                        )}
                      </SearchResultValue>
                    )}
                  </SearchResultItem>
                ))}
              </SearchResults>

              {selectedDeals.size > 0 && (
                <SelectedSummary>
                  <SelectedCount>{selectedDeals.size}</SelectedCount>{' '}
                  {t('deals.dealsSelected', 'deals selected')}
                </SelectedSummary>
              )}
            </>
          )}
        </FormGroup>
      </FormContainer>

      <FormFooter>
        <Button variant="secondary" size="medium" onClick={onClose}>
          {t('deals.cancel', 'Cancel')}
        </Button>
        <Button
          variant="primary"
          size="medium"
          onClick={handleAttachSelected}
          disabled={selectedDeals.size === 0 || isAttaching}
        >
          {isAttaching ? (
            <>
              <Loading size="small" />
              {t('deals.attaching', 'Attaching...')}
            </>
          ) : (
            t('deals.attachSelected', 'Attach Selected')
          )}
        </Button>
      </FormFooter>
    </Container>
  )
}
